import type { SongLine, Chord } from '../../types';
import type { LineGroup } from './types';

/**
 * Build display groups from raw song lines. Each group is a non-chord line
 * with any preceding chord-only lines merged above it.
 *
 * Chord position data is preserved so the renderer can align chords above
 * the corresponding syllables in the lyric line.
 */
export function buildGroups(lines: SongLine[]): LineGroup[] {
  const result: LineGroup[] = [];
  let pendingChordLines: SongLine[] = [];

  for (const line of lines) {
    if (line.type === 'chord') {
      pendingChordLines.push(line);
    } else {
      // Non-chord line: decide whether to merge pending chords or keep them separate.
      const hasLyricText = line.type !== 'section' && line.text.trim().length > 0 && !/^\[.+\]$/.test(line.text.trim());

      if (hasLyricText && pendingChordLines.length > 0) {
        // Real lyric line — check if chord lines can be merged or should stay separate.
        // When multiple chord lines have a repeating pattern (e.g. identical
        // chord patterns like "D7M Bsus2 Gsus2 Bsus2" x3 in a break), merging
        // them produces a garbled string. Detect repetition and flush separately.
        if (pendingChordLines.length > 1 && hasRepeatingPattern(pendingChordLines)) {
          const { standalone, lyricChordLine } = splitLastRepeatingLine(pendingChordLines);
          flushChordLinesSeparately(standalone, result);
          if (lyricChordLine) {
            result.push({
              chordText: formatChordText(lyricChordLine.chords!),
              chords: lyricChordLine.chords!,
              line,
            });
          } else {
            result.push({ chordText: null, chords: null, line });
          }
        } else {
          const merged = mergeChordLines(pendingChordLines);
          result.push({
            chordText: merged.text,
            chords: merged.chords,
            line,
          });
        }
      } else {
        // Empty line or section header — flush each pending chord line as its own group
        flushChordLinesSeparately(pendingChordLines, result);
        result.push({ chordText: null, chords: null, line });
      }
      pendingChordLines = [];
    }
  }

  // Trailing chord-only lines (e.g. outro) — keep each as its own group
  flushChordLinesSeparately(pendingChordLines, result);

  return result;
}

/**
 * Check if multiple chord lines contain a repeating chord pattern, indicating
 * they are separate repetitions (e.g. a break with the same chord pattern
 * repeated 3 times) rather than different chord progressions.
 *
 * Only returns true when at least two lines share the exact same chord
 * fingerprint (same chord names at the same positions).
 */
function hasRepeatingPattern(chordLines: SongLine[]): boolean {
  if (chordLines.length < 2) return false;
  const fingerprints = chordLines.map((line) => {
    if (!line.chords?.length) return '';
    return line.chords.map((c) => `${c.name}@${c.position}`).join(',');
  });
  const seen = new Set<string>();
  for (const fp of fingerprints) {
    if (!fp) continue;
    if (seen.has(fp)) return true;
    seen.add(fp);
  }
  return false;
}

/**
 * When repeating chord lines precede a lyric, the last chord line may
 * contain chords that belong to the lyric rather than the repeating pattern.
 *
 * We detect the "pattern" by building a fingerprint from the earlier lines
 * (chord name + position pairs). Chords in the last line that match a
 * pattern slot stay standalone; chords that don't appear in the pattern
 * are split off to pair with the following lyric.
 *
 * Example: lines 70="D7M Bsus2 Gsus2 Bsus2", 71="D7M Bsus2 Gsus2 Bsus2",
 * 72="D7M B5" → D7M@0 exists in the pattern → standalone. B5@10 does NOT
 * exist in the pattern → pairs with the lyric "And I wonder,".
 */
function splitLastRepeatingLine(chordLines: SongLine[]): {
  standalone: SongLine[];
  lyricChordLine: SongLine | null;
} {
  if (chordLines.length < 2) {
    return { standalone: chordLines, lyricChordLine: null };
  }

  const last = chordLines[chordLines.length - 1];
  const earlier = chordLines.slice(0, -1);

  if (!last.chords?.length) {
    return { standalone: chordLines, lyricChordLine: null };
  }

  // Build a pattern fingerprint from all earlier chord lines
  const patternSlots = new Set<string>();
  for (const line of earlier) {
    if (line.chords) {
      for (const c of line.chords) {
        patternSlots.add(`${c.name}@${c.position}`);
      }
    }
  }

  const inPattern: Chord[] = [];
  const forLyric: Chord[] = [];
  for (const chord of last.chords) {
    if (patternSlots.has(`${chord.name}@${chord.position}`)) {
      inPattern.push(chord);
    } else {
      forLyric.push(chord);
    }
  }

  if (forLyric.length === 0) {
    return { standalone: chordLines, lyricChordLine: null };
  }

  const standalone = [...earlier];
  if (inPattern.length > 0) {
    standalone.push({
      type: 'chord',
      text: formatChordText(inPattern),
      chords: inPattern,
    });
  }

  return {
    standalone,
    lyricChordLine: {
      type: 'chord',
      text: formatChordText(forLyric),
      chords: forLyric,
    },
  };
}

/**
 * Flush each pending chord line as its own standalone group (chord text + empty lyric).
 */
function flushChordLinesSeparately(chordLines: SongLine[], result: LineGroup[]): void {
  for (const cl of chordLines) {
    const text = cl.chords && cl.chords.length > 0
      ? formatChordText(cl.chords)
      : cl.text;
    result.push({
      chordText: text,
      chords: cl.chords ?? null,
      line: { type: 'lyric', text: '' },
    });
  }
}

/**
 * Build a chord text string by positioning chords at their stored character
 * positions, padding with spaces so each chord sits in the correct column.
 */
function formatChordText(chords: Chord[]): string {
  if (chords.length === 0) return '';
  const sorted = [...chords].sort((a, b) => a.position - b.position);
  let line = '';
  let pos = 0;
  for (const chord of sorted) {
    const target = Math.max(chord.position, pos);
    while (pos < target) {
      line += ' ';
      pos++;
    }
    line += chord.name;
    pos += chord.name.length;
  }
  return line;
}

/**
 * Merge one or more chord-only lines into a single chordText string and
 * a combined Chord[] array.
 */
function mergeChordLines(
  chordLines: SongLine[],
): { text: string | null; chords: Chord[] | null } {
  if (chordLines.length === 0) return { text: null, chords: null };

  const allChords: Chord[] = [];
  for (const line of chordLines) {
    if (line.chords && line.chords.length > 0) {
      allChords.push(...line.chords);
    }
  }

  if (allChords.length === 0) {
    const text = chordLines.map((l) => l.text).join('  ');
    return { text, chords: null };
  }

  return {
    text: formatChordText(allChords),
    chords: allChords,
  };
}
